import { camelCase } from 'change-case';
import type FeatureFlags from './featureFlags';

/**
 * A row of the `feature_flags[]` group used by Prismic documents to gate content.
 * `feature_flag` holds a LaunchDarkly key in any casing (`fleet-alerts`, `fleetAlerts`);
 * it must exist in the catalog (`featureFlags.ts`) to ever resolve to `true`.
 */
export type PrismicFeatureFlagEntry = { feature_flag?: string | null };

const toFlagKeys = (entries: readonly PrismicFeatureFlagEntry[] | null | undefined): string[] =>
  (entries ?? []).map((entry) => entry.feature_flag?.trim()).filter((key): key is string => Boolean(key));

/**
 * Whether a Prismic document passes its feature-flag gate: every listed flag must be ON.
 * A document with no flags listed is public. Flags outside the catalog never match, so a typo
 * hides the content instead of leaking an unreleased feature.
 */
export const matchesPrismicFeatureFlags = (entries: readonly PrismicFeatureFlagEntry[] | null | undefined, flags: Partial<FeatureFlags> | null | undefined): boolean => {
  const requiredKeys = toFlagKeys(entries);
  if (requiredKeys.length === 0) return true;
  if (!flags) return false;

  return requiredKeys.every((key) => flags[camelCase(key) as keyof FeatureFlags] === true);
};
